import React, { useState } from "react";

// field відповідає ключам, які читає applyReportFilters (../../decision/importColumns.mjs).
const FILTERS = [
  { field: "id", label: "№", type: "number" },
  { field: "pib", label: "ПІБ", type: "text" },
  { field: "room", label: "Кімната", type: "text" },
  { field: "ses", label: "Сесія", type: "number" },
  { field: "dorm", label: "Гуртожиток", type: "number" },
  { field: "act", label: "Діяльність", type: "number" },
  { field: "sum", label: "Сума", type: "number" },
  { field: "sumRank", label: "Ранг за сумою", type: "number" },
  { field: "score", label: "Показник TOPSIS", type: "number" },
  { field: "modelRank", label: "Ранг за моделлю AHP+TOPSIS", type: "number" },
  { field: "positionChange", label: "Зміна позиції", type: "number" },
  { field: "modelStatus", label: "Статус", type: "status" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "Усі" },
  { value: "retains", label: "Зберігає місце" },
  { value: "loses", label: "Втрачає місце" },
];

// Компактні стилі окремо від .input-component-with-border/.input-block
// (глобальні класи розраховані на великі форми деінде в застосунку -- тут
// 12 контролів в один блок, тож великі padding/margin звідти не годяться).
const compactInputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "3px 6px",
  fontSize: "13px",
  borderRadius: "6px",
  border: "1px solid var(--text)",
  background: "var(--main-color-2)",
  color: "var(--text)",
};

// Кнопка-перемикач блоку фільтрів без власного оформлення кнопки: займає
// рядок до "Скинути" і успадковує шрифт/колір заголовка h3.
const toggleButtonStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  width: "100%",
  padding: 0,
  border: "none",
  background: "none",
  color: "inherit",
  font: "inherit",
  textAlign: "left",
  cursor: "pointer",
};

const compactSelectStyle = {
  ...compactInputStyle,
  cursor: "pointer",
};

function isFieldActive(field, value) {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return value !== "" && !(field === "modelStatus" && value === "all");
  if (typeof value === "object") return (value.min ?? "") !== "" || (value.max ?? "") !== "";
  return false;
}

function activeFilterCount(filters) {
  return Object.entries(filters || {}).filter(([field, value]) => isFieldActive(field, value)).length;
}

function NumberRangeFilter({ label, value, onChange }) {
  const range = value || {};
  return (
    <div style={{ display: "flex", gap: "4px" }}>
      <input
        type="number"
        style={compactInputStyle}
        placeholder="від"
        aria-label={`${label}: мінімум`}
        value={range.min ?? ""}
        onChange={(e) => onChange({ ...range, min: e.target.value })}
      />
      <input
        type="number"
        style={compactInputStyle}
        placeholder="до"
        aria-label={`${label}: максимум`}
        value={range.max ?? ""}
        onChange={(e) => onChange({ ...range, max: e.target.value })}
      />
    </div>
  );
}

function FilterControl({ item, value, onChange }) {
  if (item.type === "text") {
    return (
      <input
        type="text"
        style={compactInputStyle}
        aria-label={item.label}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (item.type === "status") {
    return (
      <select
        style={compactSelectStyle}
        aria-label={item.label}
        value={value ?? "all"}
        onChange={(e) => onChange(e.target.value)}
      >
        {STATUS_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  }
  return <NumberRangeFilter label={item.label} value={value} onChange={onChange} />;
}

// Блок фільтрів над таблицею звіту (ImportReportTable.js): по одному
// компактному контролу на кожну колонку (текст/числовий діапазон
// "від-до"/статус), згорнутий за замовчуванням. Сама фільтрація
// виконується в AllocationImportPage через applyReportFilters
// (../../decision/importColumns.mjs) -- цей компонент лише показує/редагує
// стан `filters`.
// normalization/onNormalizationChange -- перемикач "Режим нормалізації:
// абсолютний / класичний" (зміна режиму потребує повторного розрахунку,
// його виконує батько). anonymize -- фільтр за кімнатою прихований.
const NORMALIZATION_OPTIONS = [
  { value: "absolute", label: "абсолютний" },
  { value: "vector", label: "класичний" },
];

const ImportReportFilters = ({
  filters,
  onFilterChange,
  onReset,
  normalization,
  onNormalizationChange,
  anonymize = false,
  disabled = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const activeCount = activeFilterCount(filters);

  return (
    <div className="input-component" style={{ padding: "8px 12px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {/* Справжня <button> замість div[role=button]: Enter/Space і фокус
            працюють нативно. Стилі скинуто, щоб вигляд лишився текстовим. */}
        <h3 className="text-header" style={{ margin: 0, flex: 1 }}>
          <button
            type="button"
            style={toggleButtonStyle}
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
          >
            <span style={{ fontSize: "12px" }}>{expanded ? "▾" : "▸"}</span>
            Фільтри{activeCount > 0 ? ` (${activeCount})` : ""}
          </button>
        </h3>
        {activeCount > 0 && (
          <button
            type="button"
            className="restore-button"
            style={{ marginLeft: "auto", padding: "2px 10px", fontSize: "12px" }}
            onClick={onReset}
          >
            Скинути
          </button>
        )}
      </div>

      {onNormalizationChange && (
        <label className="text-header" style={{ display: "block", marginTop: "8px" }}>
          Режим нормалізації:{" "}
          <select
            className="header-content-select"
            aria-label="Режим нормалізації"
            value={normalization}
            disabled={disabled}
            onChange={(e) => onNormalizationChange(e.target.value)}
          >
            {NORMALIZATION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      )}

      {expanded && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
            gap: "8px",
            marginTop: "8px",
          }}
        >
          {FILTERS.filter((item) => !(anonymize && item.field === "room")).map((item) => (
            <div key={item.field}>
              <p className="text-header" style={{ fontSize: "12px", margin: "0 0 2px" }}>
                {item.label}
              </p>
              <FilterControl
                item={item}
                value={filters[item.field]}
                onChange={(value) => onFilterChange(item.field, value)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImportReportFilters;
